import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "../../utils/appToast";
import api from "../chat/api";
import Img1 from "./Invite1.png";
import {
  AVATAR_GRADIENT,
  ErrorBanner,
  FONT,
  GRADIENT,
  InfoBanner,
  Spinner,
  fieldBox,
  inputStyle,
  isValidName,
  primaryButton,
  rise,
  useMediaQuery,
} from "./components/authFlowUi";
import "./components/authFlow.css";

const ROLE_ABILITIES = {
  admin: "can manage the workspace, its members and its projects",
  editor: "can create and edit projects and proposals with the team",
  member: "can review documents and RFPs with the team",
  reviewer: "can review proposal sections assigned to you",
};

const CLOSED_STATES = {
  expired: { title: () => "This invitation has expired", body: (inv) => `Ask ${inv.inviter.name} to send you a new one.` },
  cancelled: { title: () => "This invitation was withdrawn", body: (inv) => `Ask ${inv.inviter.name} if you still need access.` },
  accepted: { title: () => "This invitation was already used", body: (inv) => `You can open ${inv.workspace} from DocuSensa.` },
  member: { title: (inv) => `You're already in ${inv.workspace}`, body: () => "Your current role stays the same." },
  invalid: { title: () => "This invitation link isn't valid", body: () => "Check the link in your email, or ask for a new invitation." },
};

// Same steps as the sidebar's workspace switcher, so the app opens in the workspace just joined.
const switchToWorkspace = async (organizationId) => {
  if (!organizationId) return;
  try {
    const { data } = await api.get("/api/v1/org/");
    if (!Array.isArray(data)) return;
    localStorage.setItem("cachedWorkspaces", JSON.stringify(data));
    const joined = data.find((org) => String(org.id) === String(organizationId));
    if (!joined) return;
    localStorage.setItem("activeWorkspace", JSON.stringify(joined));
    localStorage.setItem("activeWorkspaceId", String(joined.id));
    Cookies.set("selected_org_id", String(joined.id), { expires: 365 });
  } catch (err) {
    console.error("Could not switch to the joined workspace:", err);
  }
};

const article = (word) => (/^[aeiou]/i.test(word) ? "an" : "a");
const firstName = (name) => (name || "").trim().split(/\s+/)[0] || name;

const Logo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <svg width="36" height="36" viewBox="0 0 34 34" fill="none" aria-hidden="true">
      <path d="M15.0656 19.5206C15.0842 18.2073 14.0454 17.0997 12.7819 17.0842C11.4391 17.0667 10.3845 18.1191 10.3705 19.4896C10.3574 20.7902 11.3673 21.8464 12.6458 21.8697C13.9531 21.8939 15.0469 20.8319 15.0656 19.5206Z" fill="#BB42AE" />
      <path d="M20.7987 17.0841C19.4653 17.0705 18.4106 18.1413 18.4153 19.506C18.419 20.793 19.4578 21.8715 20.7018 21.8803C21.9867 21.889 23.0917 20.794 23.0992 19.503C23.1076 18.2043 22.0539 17.0967 20.7987 17.0841Z" fill="#BB42AE" />
      <path d="M18.2838 25.3651C18.0507 25.5339 17.5098 25.7318 16.7564 25.7318C16.0029 25.7318 15.4621 25.5349 15.229 25.3651C14.8066 25.0596 14.2247 25.1683 13.9309 25.6076C13.6372 26.047 13.7416 26.6522 14.1641 26.9577C14.7907 27.4116 15.7362 27.6716 16.7564 27.6716C17.7765 27.6716 18.7221 27.4116 19.3487 26.9577C19.7711 26.6522 19.8756 26.048 19.5818 25.6076C19.2881 25.1673 18.7062 25.0596 18.2838 25.3651Z" fill="#BB42AE" />
      <path d="M15.8336 4.64679V8.43814C15.8336 8.97352 16.2513 9.40804 16.766 9.40804C17.2808 9.40804 17.6985 8.97352 17.6985 8.43814V4.64679C18.5219 4.27241 19.0973 3.4189 19.0973 2.42475C19.0973 1.08532 18.0538 0 16.766 0C15.4783 0 14.4348 1.08532 14.4348 2.42475C14.4348 3.4189 15.0102 4.27338 15.8336 4.64679Z" fill="#BB42AE" />
      <path d="M29.486 14.8664C29.1466 14.7733 28.9806 14.5968 28.8435 14.2893C26.8993 9.94029 23.6672 7.2042 19.2202 5.98891C19.1419 5.96757 19.0673 5.94915 18.9964 5.93848C18.8826 6.04614 18.7623 6.14604 18.6346 6.23721V8.43597C18.6346 9.50577 17.7981 10.3758 16.7696 10.3758C15.7411 10.3758 14.9046 9.50577 14.9046 8.43597V6.23721C14.7843 6.15185 14.6706 6.05777 14.5624 5.95787C14.4859 5.95981 14.4029 5.97533 14.3097 6.00055C9.87755 7.18286 6.70242 9.94417 4.74325 14.2311C4.58659 14.5735 4.43739 14.7976 4.01124 14.9033C1.40213 15.5531 -0.419025 18.1777 0.0835882 20.9942C0.47337 23.1814 2.35234 25.093 4.55675 25.5324C4.85795 25.5925 5.03885 25.7051 5.17406 26.0115C5.79697 27.4228 6.68936 28.6419 7.75427 29.7214C13.4872 35.536 23.9591 34.7106 28.1619 26.35C28.4108 25.8554 28.6421 25.4665 29.2501 25.4092C29.6072 25.3753 29.9644 25.1881 30.2945 25.0145C32.221 24.0009 33.3642 22.3861 33.4631 20.1291C33.5684 17.7189 31.861 15.5211 29.486 14.8664ZM3.72031 22.5461C2.82232 22.1804 2.13321 21.0922 2.16398 20.0137C2.19568 18.9119 2.95846 17.8944 3.72031 17.7111V22.5461ZM27.4709 20.3502C27.1995 24.6275 25.1602 27.8825 21.4265 29.7942C17.5818 31.7631 13.8155 31.3441 10.4016 28.735C7.29455 26.3607 5.73635 23.0427 6.01797 18.9992C6.32009 14.6618 8.56927 11.592 12.192 9.54942C12.5557 9.3438 12.9454 9.18861 13.3716 8.98688C13.6924 9.98102 13.8565 10.4999 14.1596 11.4436C14.5326 12.6075 15.2785 12.7132 15.4912 12.7132C18.0191 12.7132 20.2944 11.7986 22.4289 10.464C22.6788 10.3079 22.8261 10.3263 23.0387 10.5009C26.102 13.0139 27.7282 16.296 27.4709 20.3502ZM29.6762 22.5277C30.0511 20.907 30.0408 19.3444 29.7816 17.7693C30.605 18.0496 31.2027 18.9051 31.2605 19.8595C31.3351 21.0835 30.8381 21.9544 29.6762 22.5277Z" fill="#BB42AE" />
    </svg>
    <span style={{ font: `700 26px ${FONT}`, color: "#BB42AE", letterSpacing: "-.01em" }}>DocuSensa</span>
  </div>
);

const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8E8494" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const AcceptInvitation = () => {
  const navigate = useNavigate();
  const mobile = useMediaQuery("(max-width: 900px)");
  const token = Cookies.get("invitation_token");

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [fullName, setFullName] = useState("");
  const [focused, setFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/api/v1/org/invites/accept/${token}/`);
      setInvite(data);
      setFullName((current) => current || data.full_name || "");
    } catch (err) {
      if (err?.response?.status === 404) setInvite({ status: "invalid" });
      else setLoadError("We couldn't load this invitation. Please refresh the page.");
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/rfp-dashboard", { replace: true });
      return;
    }
    if (!Cookies.get("access_token")) {
      navigate(`/signin?invitation=${encodeURIComponent(token)}`, { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInAsSomeoneElse = () => navigate(`/signin?invitation=${encodeURIComponent(token)}`);

  const [leaving, setLeaving] = useState(false);

  const leave = async () => {
    setLeaving(true);
    if (invite?.status === "member") {
      await api.post(`/api/v1/org/invites/accept/${token}/`, {}).catch(() => {});
    }
    if (invite?.is_invitee && ["member", "accepted"].includes(invite.status)) {
      await switchToWorkspace(invite.organization_id);
    }
    Cookies.remove("invitation_token");
    navigate("/rfp-dashboard", { replace: true });
  };

  const canAccept = !submitting && isValidName(fullName);

  const accept = async (e) => {
    e.preventDefault();
    if (!canAccept) return;
    setSubmitting(true);
    setError("");
    try {
      const name = fullName.trim().replace(/\s+/g, " ");
      const { data } = await api.post(`/api/v1/org/invites/accept/${token}/`, { new_full_name: name });
      Cookies.remove("invitation_token");
      if (!data?.already_member) {
        localStorage.setItem("fullName", name);
        localStorage.setItem("full_name", name);
      }
      await switchToWorkspace(data?.organization_id || invite.organization_id);
      toast.success(data?.already_member ? data.message : `Welcome to ${invite.workspace}!`, { autoClose: 2000 });
      setTimeout(() => navigate("/rfp-dashboard", { replace: true }), 1200);
    } catch (err) {
      const data = err?.response?.data || {};
      setError(data.message || data.error || "We couldn't accept the invitation. Please try again.");
      if ([400, 404].includes(err?.response?.status)) load();
      setSubmitting(false);
    }
  };

  const closed = invite && CLOSED_STATES[invite.status];
  const wrongAccount = invite && invite.status === "valid" && !invite.is_invitee;
  const headline = mobile ? 34 : 52;

  const renderBody = () => {
    if (loadError) return <ErrorBanner>{loadError}</ErrorBanner>;
    if (!invite) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#6B6170", font: `500 15px ${FONT}` }}>
          <Spinner onLight /> Loading your invitation
        </div>
      );
    }
    if (closed) {
      return (
        <div style={rise}>
          <h1 style={{ margin: 0, font: `600 ${mobile ? 30 : 40}px/1.15 ${FONT}`, letterSpacing: "-.025em", color: "#171717" }}>
            {closed.title(invite)}
          </h1>
          <p style={{ margin: "16px 0 32px", font: `500 18px/1.6 ${FONT}`, color: "#52525B" }}>{closed.body(invite)}</p>
          <div style={{ width: mobile ? "100%" : 220 }}>
            <button type="button" onClick={leave} disabled={leaving} style={primaryButton(!leaving, 56)}>
              {leaving ? <Spinner /> : "Go to DocuSensa"}
            </button>
          </div>
        </div>
      );
    }

    const role = invite.role_display || "Member";
    const ability = ROLE_ABILITIES[invite.role] || "can work with the team";
    const inviterLine = [invite.inviter.email, invite.inviter.role && `${invite.inviter.role} of ${invite.workspace}`]
      .filter(Boolean)
      .join(" · ");

    return (
      <div style={rise}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div
            style={{
              flex: "none", width: 68, height: 68, borderRadius: "50%", background: AVATAR_GRADIENT,
              display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", font: `600 26px ${FONT}`,
            }}
          >
            {(invite.inviter.name || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ font: `600 19px ${FONT}`, color: "#171717" }}>{invite.inviter.name}</div>
            <div style={{ font: `500 16px ${FONT}`, color: "#52525B", overflowWrap: "anywhere" }}>{inviterLine}</div>
          </div>
        </div>

        <h1 style={{ margin: "36px 0 0", font: `600 ${headline}px/1.12 ${FONT}`, letterSpacing: "-.03em", color: "#171717", textWrap: "balance" }}>
          {firstName(invite.inviter.name)} wants you on the{" "}
          <span style={{ background: GRADIENT, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
            {invite.workspace}
          </span>{" "}
          workspace
        </h1>

        <p style={{ margin: "26px 0 0", maxWidth: 620, font: `500 ${mobile ? 16 : 19}px/1.6 ${FONT}`, color: "#52525B" }}>
          You'll join as {article(role)} <strong style={{ color: "#171717", fontWeight: 600 }}>{role}</strong> and {ability}.
        </p>

        {invite.note && (
          <p style={{ margin: "16px 0 0", maxWidth: 620, paddingLeft: 14, borderLeft: "3px solid #E3C9E6", font: `500 16px/1.6 ${FONT}`, color: "#52525B", whiteSpace: "pre-line" }}>
            “{invite.note}”
          </p>
        )}

        {wrongAccount ? (
          <div style={{ marginTop: 40, maxWidth: 680 }}>
            <InfoBanner>
              This invitation is for <strong>{invite.email}</strong>. You're signed in as <strong>{invite.signed_in_email}</strong>.
            </InfoBanner>
            <div style={{ width: mobile ? "100%" : 260, marginTop: 18 }}>
              <button type="button" onClick={signInAsSomeoneElse} style={primaryButton(true, 56)}>
                Switch account
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={accept} style={{ marginTop: 44, maxWidth: 680 }}>
            <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", gap: 16 }}>
              <div style={{ ...fieldBox(focused ? "focus" : fullName ? "filled" : "idle", mobile ? 56 : 64), flex: mobile ? "none" : 1 }}>
                <input
                  aria-label="Your full name"
                  placeholder="Your full name"
                  value={fullName}
                  maxLength={255}
                  autoComplete="name"
                  onChange={(e) => setFullName(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  style={inputStyle("0 22px", true)}
                />
              </div>
              <button type="submit" disabled={!canAccept} style={{ ...primaryButton(canAccept, 64), width: mobile ? "100%" : 172, font: `600 18px ${FONT}` }}>
                {submitting ? <Spinner /> : "Accept"}
                {!submitting && <span aria-hidden="true">→</span>}
              </button>
            </div>
            {error && <ErrorBanner style={{ marginTop: 14 }}>{error}</ErrorBanner>}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 18, font: `500 15px ${FONT}`, color: "#52525B" }}>
              <LockIcon />
              <span>Joining as</span>
              <strong style={{ color: "#171717", fontWeight: 600, overflowWrap: "anywhere" }}>{invite.email}</strong>
              <span style={{ color: "#B8AEBB" }}>·</span>
              <button
                type="button"
                className="ds-link-btn"
                onClick={signInAsSomeoneElse}
                style={{ padding: 0, border: "none", background: "none", cursor: "pointer", font: `600 15px ${FONT}`, color: "#8B55DD" }}
              >
                Not you?
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  return (
    <div
      className="ds-auth"
      style={{
        minHeight: "100vh", boxSizing: "border-box", padding: mobile ? 12 : 24, background: "#F1EFEC", fontFamily: FONT,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        style={{
          ...(mobile
            ? { flex: 1, alignSelf: "stretch" }
            : { width: "100%", maxWidth: 1600, height: "min(calc(100vh - 48px), 960px)", minHeight: 620 }),
          boxSizing: "border-box", display: "flex", gap: 32, padding: mobile ? 0 : 32, borderRadius: 18, overflow: "hidden",
          background: "linear-gradient(100deg,#FDF9F7 0%,#FCF6F6 45%,#F6E9F4 75%,#EFE4F6 100%)",
          boxShadow: "0 1px 2px rgba(0,0,0,.04), 0 12px 32px -18px rgba(60,20,70,.18)",
        }}
      >
        <div
          style={{
            flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflowY: mobile ? "visible" : "auto",
            padding: mobile ? "28px 20px" : "40px clamp(24px, 5vw, 96px)",
          }}
        >
          <Logo />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: mobile ? "40px 0" : "48px 0" }}>
            {renderBody()}
          </div>
          {invite && !closed && (
            <p style={{ margin: 0, font: `500 15px ${FONT}`, color: "#6B6170" }}>
              Didn't expect this? You can ignore it. Nothing happens until you accept.
            </p>
          )}
        </div>

        {!mobile && (
          <div
            style={{
              flex: "0 0 42%", borderRadius: 28, display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(160deg,#F5E9F6 0%,#EEE4F8 100%)", padding: 40,
            }}
          >
            <img src={Img1} alt="" style={{ width: "100%", maxWidth: 540, height: "auto", objectFit: "contain" }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitation;
